/**
 * CSV Upload Modal Component - FIXED to prevent redundant API calls
 */
import React, { useState, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { interviewService } from '../../services/interviewService';
import LoadingSpinner, { InlineLoader } from '../common/LoadingSpinner';
import { validateCSVFile } from '../../utils/validators';

const CSVUploadModal = () => {
  const { state, closeModal, addNotification } = useAppContext();
  // REMOVED: useInterviews hook to prevent redundant API calls
  // Now using interviewService directly
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // New analysis state
  const [analysisData, setAnalysisData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  
  const isOpen = state.modals.csvUpload;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateCSVFile(file);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      setSelectedFile(null);
      return;
    }

    setValidationErrors([]);
    setSelectedFile(file);
    
    // Preview first few lines
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').slice(0, 6); // First 5 rows + header
      const headers = lines[0]?.split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1, 4).map(line => 
        line.split(',').map(cell => cell.trim().replace(/"/g, ''))
      );
      
      setPreviewData({ headers, rows });
    };
    reader.readAsText(file);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setIsAnalyzing(true);
    setAnalysisData(null);

    try {
      const response = await interviewService.analyzeCSV(selectedFile);
      
      if (response.success) {
        setAnalysisData(response.data);
        setShowAnalysis(true);
      } else {
        addNotification({
          type: 'error',
          title: 'Analysis Failed',
          message: response.message || 'Failed to analyze CSV file.'
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Analysis Failed',
        message: error.message || 'Failed to analyze CSV file.'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Use interviewService directly instead of useInterviews hook
      const response = await interviewService.importCSV(selectedFile, updateExisting, (progress) => {
        setUploadProgress(progress);
      });

      addNotification({
        type: 'success',
        title: 'CSV Upload Successful',
        message: `Successfully imported ${response.data?.imported_count || 0} interviews.`
      });

      // Reset form and close modal
      handleClose();
      
      // Trigger a global refresh by dispatching a custom event
      window.dispatchEvent(new CustomEvent('interviews-updated', { 
        detail: { source: 'csv-import', count: response.data?.imported_count || 0 } 
      }));
      
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'CSV Upload Failed',
        message: error.message || 'Failed to upload CSV file.'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!isUploading && !isAnalyzing) {
      setSelectedFile(null);
      setPreviewData(null);
      setValidationErrors([]);
      setUploadProgress(0);
      setUpdateExisting(false);
      setAnalysisData(null);
      setShowAnalysis(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      closeModal('csvUpload');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        handleFileSelect({ target: { files: [file] } });
      } else {
        setValidationErrors(['Please select a CSV file']);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {showAnalysis ? 'Confirm CSV Upload' : 'Upload CSV Interviews'}
              </h3>
              <button
                onClick={handleClose}
                disabled={isUploading || isAnalyzing}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* File Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="text-green-600">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-900">
                    <span className="font-medium">{selectedFile.name}</span>
                    <span className="text-gray-500 ml-2">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-gray-400">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600">
                    <label htmlFor="csvFile" className="cursor-pointer text-blue-600 hover:text-blue-500">
                      Click to upload
                    </label>{' '}
                    or drag and drop your CSV file here
                  </div>
                  <input
                    ref={fileInputRef}
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                <div className="text-red-800 text-sm">
                  <ul className="list-disc list-inside space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* CSV Preview */}
            {previewData && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Preview (first 3 rows):</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 rounded-md">
                    <thead className="bg-gray-50">
                      <tr>
                        {previewData.headers.map((header, index) => (
                          <th key={index} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 last:border-r-0">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {previewData.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-t border-gray-200">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="px-3 py-2 text-xs text-gray-900 border-r border-gray-200 last:border-r-0">
                              {cell || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Analysis Results */}
            {showAnalysis && analysisData && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-3">📊 Upload Analysis Results</h4>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{analysisData.new_records}</div>
                    <div className="text-xs text-gray-600">New Records</div>
                    <div className="text-xs text-green-600">Will be added</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{analysisData.existing_records}</div>
                    <div className="text-xs text-gray-600">Existing Records</div>
                    <div className="text-xs text-orange-600">{updateExisting ? 'Will be updated' : 'Will be skipped'}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{analysisData.invalid_rows}</div>
                    <div className="text-xs text-gray-600">Invalid Rows</div>
                    <div className="text-xs text-red-600">Will be skipped</div>
                  </div>
                </div>

                <div className="text-sm text-gray-700">
                  <div className="flex items-center justify-between py-1">
                    <span>✅ <strong>{analysisData.new_records} new interviews</strong> will be imported</span>
                  </div>
                  {analysisData.existing_records > 0 && (
                    <div className="flex items-center justify-between py-1">
                      <span>🔄 <strong>{analysisData.existing_records} existing interviews</strong> found</span>
                      <span className="text-xs text-gray-500">
                        {updateExisting ? 'Will be updated with new data' : 'Will be skipped (enable update to change)'}
                      </span>
                    </div>
                  )}
                  {analysisData.invalid_rows > 0 && (
                    <div className="flex items-center justify-between py-1">
                      <span>⚠️ <strong>{analysisData.invalid_rows} invalid rows</strong> detected</span>
                      <span className="text-xs text-gray-500">Missing required fields</span>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="text-sm font-medium text-blue-900">
                      Summary: {analysisData.total_rows} total rows processed
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Options */}
            {selectedFile && (
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => {
                      setUpdateExisting(e.target.checked);
                      // If analysis is shown, trigger re-analysis when this changes
                      if (showAnalysis) {
                        // Clear analysis to show updated results
                        setShowAnalysis(false);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Update existing interviews (match by name, company & date)
                  </span>
                </label>
                {showAnalysis && (
                  <div className="mt-2 text-xs text-gray-500">
                    💡 Option changed - click "Analyze" again to see updated results
                  </div>
                )}
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-700 mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {!showAnalysis ? (
              // Step 1: Analyze CSV
              <>
                <button
                  onClick={handleAnalyze}
                  disabled={!selectedFile || isAnalyzing || validationErrors.length > 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? (
                    <>
                      <InlineLoader size="sm" color="white" />
                      <span className="ml-2">Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Analyze CSV
                    </>
                  )}
                </button>
                <button
                  onClick={handleClose}
                  disabled={isAnalyzing}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </>
            ) : (
              // Step 2: Confirm and Upload
              <>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading || validationErrors.length > 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <InlineLoader size="sm" color="white" />
                      <span className="ml-2">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Confirm Upload
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowAnalysis(false)}
                  disabled={isUploading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Back to File
                </button>
                <button
                  onClick={handleClose}
                  disabled={isUploading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadModal;